import basketUtils from 'utils/Basket';
import checkoutApi from 'services/api/checkout';
import checkoutUtils from 'utils/Checkout';
import localeUtils from 'utils/LanguageLocale';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';
import { PLACEHOLDER_SHIPPING_OPTION_ID } from 'constants/GooglePay';
import { normalizeEmail } from 'utils/Email';

/**
 * @typedef {Object} IntermediateAddress
 * @property {string} [countryCode]
 * @property {string} [postalCode]
 * @property {string} [administrativeArea]
 * @property {string} [locality]
 * @property {string} [sortingCode]
 */

/**
 * @typedef {Object} IntermediatePaymentData
 * @property {('SHIPPING_ADDRESS'|'SHIPPING_OPTION'|'INITIALIZE')} callbackTrigger
 * @property {IntermediateAddress} [shippingAddress]
 * @property {{id: string}} [shippingOptionData]
 */

/**
 * @typedef {Object} CheckoutContext
 * @property {string|number} orderId
 * @property {string|number} shippingGroupId - primary group; used by the mid-paysheet onPaymentDataChanged callback for fee calc + shipping-methods fetch
 * @property {string[]} [shippingGroupIds] - every shipping group that requires a physical address (HARD_GOOD + SAME_DAY + GIFT); used at onPaymentAuthorized to populate all groups before submitOrder validation
 * @property {Object<string,string>} [shippingMethodIdsByGroup] - map of shippingGroupId → default shippingMethodId for every physical group; updated by handleShippingOptionChange when the user picks for the primary group; used at onPaymentAuthorized to persist a method on every group before submitOrder validation
 * @property {Array} [shippingMethods]
 * @property {Object} [priceInfo]
 * @property {string|number} [currentShippingMethodId]
 * @property {boolean} [hasShippingOptions] - true for STH carts, false for SDD/Mixed
 * @property {string} [transactionCurrencyCode]
 */

const CALLBACK_TRIGGERS = {
    SHIPPING_ADDRESS: 'SHIPPING_ADDRESS',
    SHIPPING_OPTION: 'SHIPPING_OPTION',
    INITIALIZE: 'INITIALIZE'
};

const ERROR_REASONS = {
    OTHER_ERROR: 'OTHER_ERROR'
};

const ERROR_INTENTS = {
    SHIPPING_ADDRESS: 'SHIPPING_ADDRESS',
    SHIPPING_OPTION: 'SHIPPING_OPTION',
    PAYMENT_AUTHORIZATION: 'PAYMENT_AUTHORIZATION'
};

const TRANSACTION_STATES = {
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR'
};

const TOTAL_PRICE_STATUS = {
    // Google Pay docs recommend ESTIMATED for intermediate callback updates;
    // FINAL is reserved for the final authorized total returned to the paysheet.
    ESTIMATED: 'ESTIMATED'
};

// 3-state cart classification used in the idempotency cache key. UNKNOWN keeps
// callers that forget hasShippingOptions from colliding with a real SDD/Mixed
// entry (false) — see buildCacheKey for the why.
const CART_TYPES = {
    STH: 'STH',
    SDD: 'SDD',
    UNKNOWN: 'UNKNOWN'
};

const CACHE_TTL_MS = 30000;

// Module-level cache for INITIALIZE idempotency. Skips duplicate Cart Service
// calls when the paysheet re-invokes INITIALIZE with the same address (a known
// Google Pay behavior — fires multiple times on init/account change).
let lastRequestHash = null;
let lastResponse = null;
let lastResponseTimestamp = 0;

/**
 * Stable hash for an address used to dedupe INITIALIZE invocations. Uses only
 * the partial fields Google Pay provides during onPaymentDataChanged
 * (country/postal/state/city) — street/name/phone are not available here.
 *
 * @param {IntermediateAddress} address
 * @returns {string}
 */
function hashAddress(address) {
    if (!address) {
        return '';
    }

    return [address.countryCode, address.postalCode, address.administrativeArea, address.locality]
        .map(part => (part || '').toString().trim().toUpperCase())
        .join('|');
}

/**
 * Composite cache key combining address + session-identifying context. Prevents
 * a previous session's cached response from being returned when the new
 * session has a different order, shipping group, currency, or cart type.
 *
 * @param {IntermediateAddress} address
 * @param {CheckoutContext} context
 * @returns {string}
 */
function buildCacheKey(address, context = {}) {
    // Explicit 3-state segment so an omitted hasShippingOptions does NOT collide
    // with an SDD/Mixed cart (false). Falling back to SDD would let an STH
    // caller that forgot the flag hit an SDD-shaped cached response.
    let cartType;

    if (context.hasShippingOptions === true) {
        cartType = CART_TYPES.STH;
    } else if (context.hasShippingOptions === false) {
        cartType = CART_TYPES.SDD;
    } else {
        cartType = CART_TYPES.UNKNOWN;
    }

    return [hashAddress(address), context.orderId, context.shippingGroupId, context.transactionCurrencyCode, cartType].join('::');
}

/**
 * Maps Google Pay's IntermediateAddress shape to Sephora's Cart Service order
 * address shape. Mirrors formatPayPalShippingAddress: uppercases city/country,
 * trims postal code. Street/name/phone are not provided during
 * onPaymentDataChanged (only on onPaymentAuthorized), so they default to empty.
 *
 * @param {IntermediateAddress} intermediateAddress
 * @returns {Object} Address in checkoutApi.createShippingAddress shape
 */
function normalizeGooglePayAddress(intermediateAddress = {}) {
    return {
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: (intermediateAddress.locality || '').toUpperCase(),
        state: intermediateAddress.administrativeArea || '',
        postalCode: (intermediateAddress.postalCode || '').trim(),
        country: (intermediateAddress.countryCode || '').toUpperCase(),
        phone: ''
    };
}

/**
 * Splits Google Pay's single `name` string into first + last on the first
 * whitespace run. Multi-word last names join with single spaces.
 *
 * @param {string} fullName
 * @returns {{ firstName: string, lastName: string }}
 */
function splitFullName(fullName) {
    const [firstName = '', ...rest] = (fullName || '').trim().split(/\s+/);

    return { firstName, lastName: rest.join(' ') };
}

/**
 * Strips non-digits and takes the trailing 10 chars so a country-code-prefixed
 * number (e.g. "+1 650-555-5555") yields the local 10-digit form. Mirrors
 * formatPayPalShippingAddress's phone normalization.
 *
 * @param {string} value
 * @returns {string}
 */
function normalizePhone(value) {
    return String(value || '')
        .replace(/\D/g, '')
        .slice(-10);
}

/**
 * Maps Google Pay's full `paymentData.shippingAddress` (delivered only at
 * onPaymentAuthorized; partial during onPaymentDataChanged) to Sephora Cart
 * Service's shipping-address shape. Name split on first space; phone stripped
 * to last-10 digits; city/country uppercased; postal trimmed.
 *
 * @param {Object} paymentData - Google Pay SDK PaymentData
 * @returns {Object} Address in checkoutApi.createShippingAddress shape
 */
function formatGooglePayFullShippingAddress(paymentData) {
    const shipping = paymentData?.shippingAddress || {};
    const { firstName, lastName } = splitFullName(shipping.name);

    return {
        firstName,
        lastName,
        address1: shipping.address1 || '',
        address2: shipping.address2 || '',
        city: (shipping.locality || '').toUpperCase(),
        state: shipping.administrativeArea || '',
        postalCode: (shipping.postalCode || '').trim(),
        country: (shipping.countryCode || '').toUpperCase(),
        phone: normalizePhone(shipping.phoneNumber)
    };
}

/**
 * Maps Google Pay's `paymentData.paymentMethodData.info.billingAddress` to the
 * backend's CreateGooglePayRequestDto billingAddress shape. Field names differ
 * from the shipping-address shape (streetAddress / locality / region /
 * postalCode / country / phoneNumber). Phone is passed through unchanged — the
 * backend accepts the SDK format (e.g. "+1 650-555-5555").
 *
 * @param {Object} paymentData - Google Pay SDK PaymentData
 * @returns {Object} billingAddress for the CreateGooglePayRequestDto
 */
function formatGooglePayBillingAddress(paymentData) {
    const billing = paymentData?.paymentMethodData?.info?.billingAddress || {};
    const { firstName, lastName } = splitFullName(billing.name);
    // Single-line street: address1 + address2 joined with a space when both
    // are present, mirroring ApplePay's formatAddress (addressLines.join(' ')).
    const streetAddress = [billing.address1, billing.address2].filter(Boolean).join(' ');

    return {
        firstName,
        lastName,
        streetAddress,
        locality: billing.locality || '',
        region: billing.administrativeArea || '',
        postalCode: (billing.postalCode || '').trim(),
        country: (billing.countryCode || '').toUpperCase(),
        phoneNumber: billing.phoneNumber || ''
    };
}

/**
 * Builds the `googlePay` payload for `POST /api/checkout/orders/creditCard`.
 * The `credential.token` is the encrypted payment-gateway token bundle (a
 * JSON string) — passed through byte-identical because the backend stores it
 * as is, and forwards it to Payment Service (Chase) for authorization.
 * Never parse/re-stringify.
 *
 * @param {Object} paymentData - Google Pay SDK PaymentData
 * @returns {Object} googlePay payload (credential / display / billingAddress)
 */
function buildInitializeGooglePayPayload(paymentData) {
    const tokenization = paymentData?.paymentMethodData?.tokenizationData || {};
    const info = paymentData?.paymentMethodData?.info || {};

    return {
        credential: {
            type: tokenization.type || '',
            token: tokenization.token || ''
        },
        display: {
            brand: info.cardNetwork || '',
            lastDigits: info.cardDetails || '',
            description: paymentData?.paymentMethodData?.description || '',
            cardFundingSource: info.cardFundingSource || ''
        },
        billingAddress: formatGooglePayBillingAddress(paymentData)
    };
}

/**
 * Localised, fallback-safe getter for the generic error message returned to
 * the paysheet when Cart Service fails without a surfaceable message.
 *
 * @returns {string}
 */
function getGenericErrorMessage() {
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return getText('googlePayGenericError');
}

/**
 * Builds Google Pay's PaymentDataError shape. The paysheet renders the message
 * inline; reason/intent drive its built-in error UX.
 *
 * @param {string} message
 * @param {string} [intent=SHIPPING_ADDRESS]
 * @returns {{ error: { reason: string, message: string, intent: string } }}
 */
function buildPaymentDataError(message, intent = ERROR_INTENTS.SHIPPING_ADDRESS) {
    return {
        error: {
            reason: ERROR_REASONS.OTHER_ERROR,
            message: message || getGenericErrorMessage(),
            intent
        }
    };
}

/**
 * Builds Google Pay's PaymentAuthorizationResult ERROR shape. Pinned to the
 * PAYMENT_AUTHORIZATION intent (the only intent valid for onPaymentAuthorized).
 * The paysheet renders the message inline and stays open so the customer can
 * pick a different card and retry.
 *
 * @param {string} message
 * @returns {{ transactionState: string, error: { reason: string, message: string, intent: string } }}
 */
function buildPaymentAuthorizationError(message) {
    return {
        transactionState: TRANSACTION_STATES.ERROR,
        error: {
            reason: ERROR_REASONS.OTHER_ERROR,
            message: message || getGenericErrorMessage(),
            intent: ERROR_INTENTS.PAYMENT_AUTHORIZATION
        }
    };
}

/**
 * Builds Google Pay's newTransactionInfo. Per the ticket, the paysheet main
 * line shows TOTAL only — do NOT include displayItems. totalPriceStatus is
 * ESTIMATED because this runs during onPaymentDataChanged callbacks (the
 * total is still subject to further changes before authorization).
 *
 * Total-source priority:
 *   1. priceInfo.maxAmountToBeAuthorized — the BE-computed authorization
 *      ceiling (BOPIS with item substitution, PAY-7598).
 *   2. priceInfo.orderTotal — tax/fee-inclusive final total.
 * Falls back to '0.00' when neither is present because Google Pay rejects
 * an empty or non-numeric totalPrice string with a paysheet error.
 *
 * @param {Object} priceInfo - from getOrderDetails
 * @param {string} transactionCurrencyCode - ISO 4217 (e.g. 'USD', 'CAD')
 * @returns {Object} newTransactionInfo
 */
function buildTransactionInfo(priceInfo = {}, transactionCurrencyCode) {
    // Ceiling wins only when strictly positive — a 0/missing max means "no
    // substitution head-room", so defer to orderTotal (which can itself be 0.00
    // for a legitimate free order after discounts). `??` alone would let a
    // literal 0 suppress a positive orderTotal.
    const parseMoney = raw => Number(basketUtils.removeCurrency(raw == null ? '' : String(raw)));
    const numericMax = parseMoney(priceInfo.maxAmountToBeAuthorized);
    const rawTotal = Number.isFinite(numericMax) && numericMax > 0 ? priceInfo.maxAmountToBeAuthorized : priceInfo.orderTotal;
    const numericTotal = parseMoney(rawTotal);
    // Google Pay rejects non-numeric or empty totalPrice strings. Force a
    // 2-fraction-digit decimal string (e.g. "125.50" not "125.5") which is
    // what its spec expects for money values.
    // isFinite (not !isNaN) — guards against Infinity/-Infinity too, otherwise
    // Infinity.toFixed(2) throws RangeError and crashes the handler.
    const totalPrice = Number.isFinite(numericTotal) && numericTotal > 0 ? numericTotal.toFixed(2) : '0.00';
    const isCanada = localeUtils.isCanada();
    // Fall back to the locale's default currency when the caller didn't supply
    // one — Google Pay rejects an undefined currencyCode and aborts the paysheet.
    const currencyCode = transactionCurrencyCode || (isCanada ? 'CAD' : 'USD');

    return {
        countryCode: isCanada ? 'CA' : 'US',
        currencyCode,
        totalPriceStatus: TOTAL_PRICE_STATUS.ESTIMATED,
        totalPrice
    };
}

/**
 * Builds Google Pay's newShippingOptionParameters from Cart Service shipping
 * methods. The default flag from Cart Service drives defaultSelectedOptionId;
 * if none flagged, falls back to the first option to avoid an empty selection.
 *
 * @param {Array} shippingMethods - from checkoutApi.getAvailableShippingMethods
 * @returns {{ defaultSelectedOptionId: string, shippingOptions: Array }}
 */
function buildShippingOptionParameters(shippingMethods = []) {
    const shippingOptions = shippingMethods.map(method => ({
        id: String(method.shippingMethodId),
        label: method.shippingMethodType,
        description: [method.shippingMethodDescription, checkoutUtils.setShippingFee(method.shippingFee)].filter(Boolean).join(' — ')
    }));

    const defaultMethod = shippingMethods.find(method => method.isDefault) || shippingMethods[0];
    const defaultSelectedOptionId = defaultMethod ? String(defaultMethod.shippingMethodId) : '';

    return { defaultSelectedOptionId, shippingOptions };
}

/**
 * Resets the INITIALIZE idempotency cache. Called when a new paysheet session
 * starts (startCheckout) so a previous session's response is not reused.
 */
function resetCache() {
    lastRequestHash = null;
    lastResponse = null;
    lastResponseTimestamp = 0;
}

function getCachedResponse(hash) {
    if (!hash || hash !== lastRequestHash || !lastResponse) {
        return null;
    }

    if (Date.now() - lastResponseTimestamp > CACHE_TTL_MS) {
        // Drop the stale entry so it doesn't linger in memory or surface during
        // debugging of subsequent sessions.
        resetCache();

        return null;
    }

    // Return a deep copy so any downstream mutation of the response object
    // cannot corrupt the cached entry that later callers will read.
    return structuredClone(lastResponse);
}

function setCachedResponse(hash, response) {
    lastRequestHash = hash;
    // Store a deep copy so a later mutation of the caller's response object
    // cannot corrupt the cache entry that future hits will read.
    lastResponse = structuredClone(response);
    lastResponseTimestamp = Date.now();
}

/**
 * Handles a shipping address change. Composes existing Cart Service endpoints:
 *   createShippingAddress → (STH only) getAvailableShippingMethods →
 *   getOrderDetails. STH carts return newShippingOptionParameters with the
 *   Cart Service default flagged; SDD/Mixed carts return total only.
 *
 * @param {IntermediateAddress} intermediateAddress
 * @param {CheckoutContext} context
 * @returns {Promise<Object>} PaymentDataRequestUpdate
 */
async function handleShippingAddressChange(intermediateAddress, context) {
    if (!context?.shippingGroupId || !context?.orderId) {
        return buildPaymentDataError(getGenericErrorMessage(), ERROR_INTENTS.SHIPPING_ADDRESS);
    }

    const address = normalizeGooglePayAddress(intermediateAddress);

    // Cart Service expects { shippingGroupId, address, saveToProfile } — passing
    // the flat address alone returns errorFields: ['address', 'shippingGroupId'].
    await checkoutApi.createShippingAddress({
        shippingGroupId: context.shippingGroupId,
        address,
        saveToProfile: false
    });

    let shippingOptionParameters;

    // Strict === true so an omitted/undefined flag is NOT treated as STH and
    // does NOT silently skip the methods fetch (mirrors buildCacheKey's
    // 3-state semantics — STH | SDD | UNKNOWN).
    if (context.hasShippingOptions === true) {
        const methodsResponse = await checkoutApi.getAvailableShippingMethods(context.orderId, context.shippingGroupId);
        const shippingMethods = methodsResponse?.shippingMethods || [];

        shippingOptionParameters = buildShippingOptionParameters(shippingMethods);
    }

    const orderDetails = await checkoutApi.getOrderDetails(context.orderId);

    const update = {
        newTransactionInfo: buildTransactionInfo(orderDetails?.priceInfo, context.transactionCurrencyCode)
    };

    if (shippingOptionParameters) {
        update.newShippingOptionParameters = shippingOptionParameters;
    }

    return update;
}

/**
 * Handles a shipping option (method) change. Calls setShippingMethod and
 * returns total only — the option list itself does not change.
 *
 * @param {string} shippingMethodId
 * @param {CheckoutContext} context
 * @returns {Promise<Object>} PaymentDataRequestUpdate (newTransactionInfo only)
 */
async function handleShippingOptionChange(shippingMethodId, context) {
    if (!context?.shippingGroupId || !context?.orderId) {
        return buildPaymentDataError(getGenericErrorMessage(), ERROR_INTENTS.SHIPPING_OPTION);
    }

    // Defensive: the placeholder id is a sentinel emitted by
    // getShippingOptionParametersOrPlaceholder when no real methods are known
    // at paysheet open (PAY-7688). It should never be the payload for a real
    // SHIPPING_OPTION callback — the INITIALIZE / SHIPPING_ADDRESS callback
    // always replaces the placeholder before the user can pick an option — but
    // if it ever reaches here, do not send it to Cart Service (setShippingMethod
    // would reject an unknown id).
    if (shippingMethodId === PLACEHOLDER_SHIPPING_OPTION_ID) {
        return {};
    }

    await checkoutApi.setShippingMethod({
        orderId: context.orderId,
        shippingGroupId: context.shippingGroupId,
        shippingMethodId
    });

    // Mirror the user's pick into the per-group map so the onPaymentAuthorized
    // setShippingMethod fan-out uses it instead of the default captured at
    // startCheckout (the paysheet only emits SHIPPING_OPTION for the primary
    // group; non-primary groups stay on their defaults).
    if (context.shippingMethodIdsByGroup) {
        context.shippingMethodIdsByGroup[context.shippingGroupId] = shippingMethodId;
    }

    const orderDetails = await checkoutApi.getOrderDetails(context.orderId);

    return {
        newTransactionInfo: buildTransactionInfo(orderDetails?.priceInfo, context.transactionCurrencyCode)
    };
}

/**
 * Handles the INITIALIZE trigger. Idempotent: returns a cached response for a
 * repeated address, and returns an empty update when no address is present
 * (lets the paysheet keep its original PaymentDataRequest).
 *
 * @param {IntermediatePaymentData} intermediatePaymentData
 * @param {CheckoutContext} context
 * @returns {Promise<Object>} PaymentDataRequestUpdate (possibly empty)
 */
async function handleInitialize(intermediatePaymentData, context) {
    const address = intermediatePaymentData?.shippingAddress;

    if (!address) {
        return {};
    }

    const cacheKey = buildCacheKey(address, context);
    const cached = getCachedResponse(cacheKey);

    if (cached) {
        return cached;
    }

    const response = await handleShippingAddressChange(address, context);

    setCachedResponse(cacheKey, response);

    return response;
}

/**
 * Cart Service rejects with an `errorMessages` array of localized strings
 * Joins them with '. ' into a single string for the paysheet's inline message.
 *
 * @param {Object} err
 * @returns {string|null}
 */
function surfaceCartError(err) {
    if (Array.isArray(err?.errorMessages) && err.errorMessages.length > 0) {
        return err.errorMessages.join('. ');
    }

    return err?.errorMessage || err?.message || null;
}

/**
 * Shared dispatcher for Google Pay's onPaymentDataChanged callback. Wired in
 * by PAY-6706 (STH) and PAY-6708 (SDD/Mix). Returns a PaymentDataRequestUpdate
 * on success or a PaymentDataError on failure — never throws so the paysheet
 * stays in a recoverable state.
 *
 * @param {IntermediatePaymentData} intermediatePaymentData
 * @param {CheckoutContext} context - resolved by the caller (orderId,
 *   shippingGroupId, hasShippingOptions, transactionCurrencyCode)
 * @returns {Promise<Object>} PaymentDataRequestUpdate or PaymentDataError
 */
async function onPaymentDataChanged(intermediatePaymentData, context) {
    try {
        if (!intermediatePaymentData || !context) {
            return buildPaymentDataError(getGenericErrorMessage());
        }

        const trigger = intermediatePaymentData.callbackTrigger;

        if (trigger === CALLBACK_TRIGGERS.SHIPPING_ADDRESS) {
            return await handleShippingAddressChange(intermediatePaymentData.shippingAddress, context);
        }

        if (trigger === CALLBACK_TRIGGERS.SHIPPING_OPTION) {
            const shippingMethodId = intermediatePaymentData.shippingOptionData?.id;

            if (!shippingMethodId) {
                return buildPaymentDataError(getGenericErrorMessage(), ERROR_INTENTS.SHIPPING_OPTION);
            }

            return await handleShippingOptionChange(shippingMethodId, context);
        }

        if (trigger === CALLBACK_TRIGGERS.INITIALIZE) {
            return await handleInitialize(intermediatePaymentData, context);
        }

        // Unknown trigger — return empty update so the paysheet keeps original state
        return {};
    } catch (err) {
        Sephora?.logger?.error('[GooglePayHandlers] onPaymentDataChanged failed', err);

        const surfacedMessage = surfaceCartError(err);
        const isOptionTrigger = intermediatePaymentData?.callbackTrigger === CALLBACK_TRIGGERS.SHIPPING_OPTION;
        const intent = isOptionTrigger ? ERROR_INTENTS.SHIPPING_OPTION : ERROR_INTENTS.SHIPPING_ADDRESS;

        return buildPaymentDataError(surfacedMessage, intent);
    }
}

/**
 * Shared Google Pay onPaymentAuthorized handler. Runs the three-call auth
 * sequence and maps the outcome to Google Pay's PaymentAuthorizationResult:
 *   1. createShippingAddress — STH/SDD only; uploads the full shipping address.
 *   2. initializeGooglePayCheckout — POSTs the SDK token bundle + billing
 *      address to the GooglePayPayment
 *   3. placeOrder — submits the order. Body is thin (no `googlePay` key) —
 *      Cart Service reads the token from step 2.
 *
 * Any rejection short-circuits the remaining steps and surfaces as ERROR
 * inline in the paysheet so the customer can retry.
 *
 * Side effect on success: writes the placeOrder response to
 * `context.orderResponse` so the per-flow wrapper can read it after the
 * paysheet closes and pass it to `checkoutUtils.placeOrderSuccess` for the
 * confirmation-page redirect (PAY-6707).
 *
 * @param {Object} paymentData - Google Pay SDK PaymentData
 * @param {CheckoutContext} context - checkout context (orderId at minimum)
 * @returns {Promise<{ transactionState: string, error?: Object }>}
 */
async function onPaymentAuthorized(paymentData, context) {
    try {
        if (paymentData?.shippingAddress) {
            // Cart Service requires every hard-good-based shipping group
            // (HARD_GOOD, SAME_DAY, GIFT) to carry the full address — gift
            // cards live in their own GiftCardShippingGroup and submitOrder
            // rejects with missing-field errors if any group is left blank.
            // shippingGroupIds is populated in startCheckout; fall back to
            // the legacy singular id when absent.
            const ids = context?.shippingGroupIds?.length ? context.shippingGroupIds : [context?.shippingGroupId].filter(Boolean);

            if (ids.length) {
                const email = normalizeEmail(paymentData?.email);
                const address = {
                    ...formatGooglePayFullShippingAddress(paymentData),
                    ...(email && { shipEmail: email })
                };

                await Promise.all(
                    ids.map(shippingGroupId =>
                        checkoutApi.createShippingAddress({
                            shippingGroupId,
                            address,
                            saveToProfile: false
                        })
                    )
                );
            }
        }

        // Persist a shipping method on every physical group. The Google Pay
        // paysheet only emits a SHIPPING_OPTION callback when the user
        // explicitly changes the option, so for auto-confirmed defaults and
        // for non-primary groups (GIFT) we need to set the method here or
        // submitOrder rejects with `checkout.order.incomplete`. The primary
        // group's entry in shippingMethodIdsByGroup reflects the user's pick
        // when handleShippingOptionChange updated it; otherwise it's the
        // default captured at startCheckout.
        const methodsByGroup = context?.shippingMethodIdsByGroup;
        const methodEntries = methodsByGroup ? Object.entries(methodsByGroup).filter(([, methodId]) => methodId) : [];

        if (methodEntries.length) {
            await Promise.all(
                methodEntries.map(([shippingGroupId, shippingMethodId]) =>
                    checkoutApi.setShippingMethod({
                        orderId: context.orderId,
                        shippingGroupId,
                        shippingMethodId
                    })
                )
            );
        }

        await checkoutApi.initializeGooglePayCheckout(buildInitializeGooglePayPayload(paymentData));

        const response = await checkoutApi.placeOrder({
            orderId: context?.orderId,
            originOfOrder: 'web',
            isExpressCheckout: true
        });

        if (context) {
            context.orderResponse = response;
        }

        return { transactionState: TRANSACTION_STATES.SUCCESS };
    } catch (err) {
        Sephora?.logger?.error('[GooglePayHandlers] onPaymentAuthorized failed', err);

        const message = surfaceCartError(err) || getGenericErrorMessage();

        return buildPaymentAuthorizationError(message);
    }
}

export {
    CALLBACK_TRIGGERS,
    ERROR_REASONS,
    ERROR_INTENTS,
    TRANSACTION_STATES,
    CART_TYPES,
    onPaymentDataChanged,
    onPaymentAuthorized,
    resetCache,
    // Exported for testing
    normalizeGooglePayAddress,
    formatGooglePayFullShippingAddress,
    formatGooglePayBillingAddress,
    buildInitializeGooglePayPayload,
    splitFullName,
    normalizePhone,
    hashAddress,
    buildCacheKey,
    buildPaymentDataError,
    buildPaymentAuthorizationError,
    buildTransactionInfo,
    buildShippingOptionParameters,
    handleShippingAddressChange,
    handleShippingOptionChange,
    handleInitialize,
    surfaceCartError
};
